import { Link } from "react-router-dom";
import { Flag, Users, HeartHandshake } from "lucide-react";
import { SupporterForm } from "@/components/SupporterForm";
import { LOGO_URL, IMAGES } from "@/constants/campaign";

const scrollToForm = () => {
  document.getElementById("supporter-form")?.scrollIntoView({ behavior: "smooth" });
};

const TriColorBar = () => (
  <div className="flex h-1.5">
    <div className="flex-1 bg-[#FF9933]" />
    <div className="flex-1 bg-white" />
    <div className="flex-1 bg-[#138808]" />
  </div>
);

const Header = () => (
  <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
    <TriColorBar />
    <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
      <a href="#home" data-testid="header-logo" className="flex items-center gap-3">
        <img
          src={LOGO_URL}
          alt="भाजपा की अर्चना लोगो"
          className="h-11 w-11 rounded-full object-cover border border-slate-200"
        />
        <span className="font-heading text-xl md:text-2xl font-semibold text-slate-900">
          भाजपा की <span className="text-[#FF9933]">अर्चना</span>
        </span>
      </a>
      <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-600">
        <a data-testid="nav-about" href="#about" className="hover:text-[#FF9933] transition-colors duration-300">
          परिचय
        </a>
        <a data-testid="nav-gallery" href="#gallery" className="hover:text-[#FF9933] transition-colors duration-300">
          गैलरी
        </a>
        <Link data-testid="nav-privacy" to="/privacy-policy" className="hover:text-[#FF9933] transition-colors duration-300">
          गोपनीयता नीति
        </Link>
      </nav>
      <button
        data-testid="header-cta-button"
        onClick={scrollToForm}
        className="bg-[#FF9933] hover:bg-[#E68A2E] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors duration-300"
      >
        समर्थन दें
      </button>
    </div>
  </header>
);

const Hero = () => (
  <section id="home" className="relative bg-[#FFF5ED] overflow-hidden">
    <img
      src={IMAGES.heroFlag}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
    />
    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center min-h-[80vh] px-4 py-12 md:px-8 max-w-7xl mx-auto">
      <div className="animate-fade-slide-up">
        <div className="flex h-2 w-28 rounded-full overflow-hidden">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-slate-200" />
          <div className="flex-1 bg-[#138808]" />
        </div>
        <h1
          data-testid="hero-headline"
          className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mt-5 leading-tight"
        >
          हरियाणा के विकास में <span className="text-[#FF9933]">भागीदार</span> बनें
        </h1>
        <p className="font-heading text-xl md:text-2xl font-semibold text-[#138808] mt-3">
          श्रीमती अर्चना गुप्ता जी के साथ, हरियाणा के संकल्प के साथ
        </p>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 mt-4 max-w-xl">
          एक सशक्त और समृद्ध हरियाणा के निर्माण में अपना योगदान दें। आज ही अपना समर्थन दर्ज करें।
        </p>
        <ul className="mt-6 space-y-3">
          {[
            { icon: Flag, text: "हरियाणा के हर जिले तक अभियान की पहुँच" },
            { icon: Users, text: "कार्यकर्ताओं और समर्थकों का बढ़ता परिवार" },
            { icon: HeartHandshake, text: "जनता की आवाज़, जनता के साथ" },
          ].map(({ icon: Icon, text }, i) => (
            <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
              <span className="h-9 w-9 rounded-lg bg-[#138808]/10 text-[#138808] flex items-center justify-center shrink-0">
                <Icon size={18} />
              </span>
              {text}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex gap-3">
          {IMAGES.gallery.map((img, i) => (
            <img
              key={img.url}
              src={img.url}
              alt={img.alt}
              className="h-20 w-28 md:h-24 md:w-36 rounded-lg object-cover shadow-md border-2 border-white hover:-translate-y-1 transition-transform duration-300 ease-out"
            />
          ))}
        </div>
      </div>
      <div id="supporter-form" className="animate-fade-slide-up" style={{ animationDelay: "0.15s" }}>
        <SupporterForm />
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="about-section" className="py-16 md:py-24 px-4 md:px-8 max-w-7xl mx-auto">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div>
        <p className="text-sm font-semibold tracking-wide uppercase text-[#FF9933]">परिचय</p>
        <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-slate-800 mt-2">
          डॉ. अर्चना गुप्ता के बारे में
        </h2>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 mt-4">
          डॉ. अर्चना गुप्ता भारतीय जनता पार्टी हरियाणा की प्रदेश अध्यक्ष हैं। 28 मई 2026 को यह
          जिम्मेदारी संभालकर वह 43 वर्षों में हरियाणा भाजपा की कमान संभालने वाली दूसरी महिला
          बनीं — इससे पहले 1980 में डॉ. कमला वर्मा यह पद संभाल चुकी हैं।
        </p>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 mt-4">
          पानीपत (हरियाणा) से ताल्लुक रखने वाली डॉ. गुप्ता पेशे से रेडियोलॉजिस्ट हैं और पीजीआई
          रोहतक से एमडी में गोल्ड मेडलिस्ट रही हैं। उनके दादा लाला सूरजभान स्वतंत्रता सेनानी
          थे। संगठन और जनसेवा का यह सफर विश्व हिंदू परिषद से शुरू होकर भाजपा के शीर्ष पद तक
          पहुँचा।
        </p>
        <ul className="mt-6 space-y-3">
          {[
            { year: "2020", text: "पानीपत की पहली महिला भाजपा जिला अध्यक्ष नियुक्त" },
            { year: "2024", text: "हरियाणा भाजपा की प्रदेश महामंत्री बनीं" },
            { year: "2026", text: "हरियाणा भाजपा की प्रदेश अध्यक्ष — पदभार 'पंचकमल', पंचकूला से" },
          ].map(({ year, text }) => (
            <li key={year} className="flex items-start gap-4">
              <span className="font-heading text-lg font-bold text-[#FF9933] w-14 shrink-0">{year}</span>
              <span className="text-slate-700 font-medium leading-relaxed">{text}</span>
            </li>
          ))}
        </ul>
        <p className="text-base md:text-lg leading-relaxed text-slate-700 mt-6">
          प्रदेश अध्यक्ष के रूप में उनकी प्राथमिकताएँ हैं — संगठनात्मक समन्वय, बूथ स्तर पर
          पार्टी को मजबूत करना और महिला सशक्तिकरण।
        </p>
      </div>
      <img
        src={IMAGES.aboutAccent}
        alt="डॉ. अर्चना गुप्ता भाजपा कार्यक्रम में संबोधित करती हुईं"
        data-testid="about-image"
        className="rounded-lg shadow-lg w-full object-cover aspect-[4/3] hover:-translate-y-1 transition-transform duration-300 ease-out"
      />
    </div>
  </section>
);

const Gallery = () => (
  <section id="gallery" data-testid="gallery-section" className="bg-[#FFF5ED] py-16 md:py-24 px-4 md:px-8">
    <div className="max-w-7xl mx-auto">
      <p className="text-sm font-semibold tracking-wide uppercase text-[#FF9933]">गैलरी</p>
      <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-slate-800 mt-2">
        अभियान की झलकियाँ
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
        {IMAGES.gallery.map((img, i) => (
          <figure
            key={img.url}
            className="group rounded-lg overflow-hidden shadow-md bg-white hover:-translate-y-1 transition-transform duration-300 ease-out"
          >
            <img
              src={img.url}
              alt={img.alt}
              data-testid={`gallery-image-${i + 1}`}
              className="w-full aspect-[4/3] object-cover"
              loading="lazy"
            />
            <figcaption className="p-4 font-heading text-lg font-semibold text-slate-800">
              {img.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="bg-[#FF9933] py-14 px-4 md:px-8">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
        आज ही अभियान से जुड़ें
      </h2>
      <p className="text-white/90 text-base md:text-lg mt-3 leading-relaxed">
        आपका एक समर्थन हरियाणा के बेहतर भविष्य की दिशा में एक कदम है।
      </p>
      <button
        data-testid="cta-join-button"
        onClick={scrollToForm}
        className="mt-6 bg-white text-[#E68A2E] font-semibold px-8 py-3 rounded-lg shadow-lg hover:-translate-y-1 transition-transform duration-300 ease-out"
      >
        समर्थक बनें
      </button>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-slate-900 text-slate-300 py-12 px-4 md:px-8">
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
      <div>
        <p className="font-heading text-xl font-semibold text-white">
          भाजपा की <span className="text-[#FF9933]">अर्चना</span>
        </p>
        <p className="text-sm mt-2 text-slate-400">bjpkiarchana.com</p>
      </div>
      <div className="text-sm space-y-2">
        <p className="font-semibold text-white">लिंक</p>
        <a href="#about" className="block hover:text-[#FF9933] transition-colors duration-300">परिचय</a>
        <a href="#gallery" className="block hover:text-[#FF9933] transition-colors duration-300">गैलरी</a>
        <Link to="/privacy-policy" className="block hover:text-[#FF9933] transition-colors duration-300">
          गोपनीयता नीति
        </Link>
      </div>
      <div className="text-sm space-y-2">
        <p className="font-semibold text-white">संपर्क</p>
        <a
          href="mailto:admin@bjpkiarchana.com"
          data-testid="footer-email"
          className="block hover:text-[#FF9933] transition-colors duration-300"
        >
          admin@bjpkiarchana.com
        </a>
      </div>
    </div>
    <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-slate-700 text-xs text-slate-500">
      © {new Date().getFullYear()} भाजपा की अर्चना अभियान। सर्वाधिकार सुरक्षित।
    </div>
  </footer>
);

export default function Home() {
  return (
    <main data-testid="home-page" className="min-h-screen bg-white">
      <Header />
      <Hero />
      <About />
      <Gallery />
      <CtaBand />
      <Footer />
    </main>
  );
}
